import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'clsx';
import { ArrowBackIosNewRounded, ArrowForwardIosRounded } from '@mui/icons-material';

import { ChannelType } from '@/modules/common/types/channel';

import { BreakpointsContext } from '../../../common/components/BreakpointsProvider/helpers';
import * as commonSelectors from '../../../common/redux/selectors/common';
import { useSlideDimensions } from '../../helpers/carousel';
import { debounce } from '@/modules/common/helpers/events';
import { useTranslate } from '@/modules/common/helpers/i18n';
import { isInRange } from '@/modules/common/helpers/number';

import CarouselSkeleton from './CarouselSkeleton/CarouselSkeleton';
import Slide from './Slide/Slide';
import { Button } from '@/mui/components';

import styles from './Carousel.module.scss';

type Props = {
  activeSlideMediaId?: string;
  onSlideClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, index: number, mediaId: string) => void;
  onSlideDoubleClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, index: number, mediaId: string) => void;
  channel: ChannelType;
  activeCustomContent?: React.ReactNode;
};

export type CarouselRef = {
  scrollTo: (currentIndex: number) => number;
};

const Carousel = forwardRef<CarouselRef, Props>(
  (
    {
      activeSlideMediaId = '',
      onSlideClick = () => null,
      onSlideDoubleClick = () => null,
      channel,
      activeCustomContent,
    },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const carouselRef = useRef<HTMLDivElement>(null);
    const breakpointList = useContext(BreakpointsContext);
    const rtl = useSelector(commonSelectors.rtlSelector);
    const t = useTranslate();

    const dimensions = useSlideDimensions(channel.design, channel.size);

    const totalSlideWidth = dimensions.width + dimensions.padding;

    const slides = channel.playlist;

    const [deltaScroll, setDeltaScroll] = useState(1);

    const [scrollMeta, setScrollMeta] = useState({
      scrollPrev: false,
      scrollNext: false,
      visibleStart: 0,
      visibleEnd: 0,
    });

    const scrollFrame = useRef(0);

    const scrollToIndex = useCallback(
      (index: number) => {
        if (!carouselRef.current) {
          return;
        }

        carouselRef.current.scrollTo({
          left: Math.abs(index * totalSlideWidth) * (rtl ? -1 : 1),
          behavior: 'smooth',
        });
      },
      [totalSlideWidth, rtl],
    );

    const updateScrollState = useCallback(() => {
      if (!carouselRef.current) {
        return;
      }
      const scrollLeft = Math.abs(carouselRef.current.scrollLeft);
      const width = carouselRef.current.clientWidth;

      const totalWidth = slides.length * totalSlideWidth - dimensions.padding;
      const visibleSlides = Math.floor(width / totalSlideWidth);

      window.cancelAnimationFrame(scrollFrame.current);
      scrollFrame.current = window.requestAnimationFrame(() => {
        setDeltaScroll(Math.floor(width / totalSlideWidth));

        setScrollMeta((prev) => {
          const scrollPrev = rtl ? scrollLeft + width < totalWidth : scrollLeft > 0;
          const scrollNext = rtl ? scrollLeft > 0 : scrollLeft + width < totalWidth;
          const visibleStart = Math.floor(scrollLeft / totalSlideWidth) - visibleSlides;
          const visibleEnd = Math.floor((scrollLeft + width) / totalSlideWidth) + visibleSlides;

          if (
            prev.scrollPrev === scrollPrev &&
            prev.scrollNext === scrollNext &&
            prev.visibleStart === visibleStart &&
            prev.visibleEnd === visibleEnd
          ) {
            return prev;
          }

          return {
            scrollPrev,
            scrollNext,
            visibleStart,
            visibleEnd,
          };
        });
      });
    }, [slides.length, totalSlideWidth, rtl, dimensions.padding]);

    useEffect(() => {
      updateScrollState();
      const node = carouselRef.current;

      const handleResize = () => {
        if (!wrapperRef.current) {
          return;
        }

        updateScrollState();
      };

      const debouncedResize = debounce(handleResize, 100);

      if (node) {
        node.addEventListener('scroll', updateScrollState);
      }
      window.addEventListener('resize', debouncedResize);

      return () => {
        if (node) {
          node.removeEventListener('scroll', updateScrollState);
        }
        window.removeEventListener('resize', debouncedResize);
        window.cancelAnimationFrame(scrollFrame.current);
      };
    }, [updateScrollState, breakpointList, channel.design, channel.size]);

    const scrollBySlides = useCallback(
      (direction: 1 | -1) => {
        if (!carouselRef.current) {
          return;
        }

        carouselRef.current.scrollTo({
          left: carouselRef.current.scrollLeft + deltaScroll * totalSlideWidth * direction,
          behavior: 'smooth',
        });
      },
      [rtl, deltaScroll, totalSlideWidth],
    );

    useImperativeHandle(ref, () => ({
      scrollTo: (currentIndex) => {
        let next = currentIndex + 1;

        next = next > slides.length - 1 ? 0 : next;

        scrollToIndex(next);

        return next;
      },
    }));

    return (
      <div className={styles.Wrapper} ref={wrapperRef}>
        <Button
          className={classNames(styles.Arrow, styles.Arrow___prev)}
          variant="text"
          shape="square"
          color="inherit"
          size="xSmall"
          aria-label={t.prevSlideVideos.text()}
          disabled={!scrollMeta.scrollPrev}
          onClick={() => scrollBySlides(-1)}
          style={{
            height: dimensions.height,
          }}
        >
          <ArrowBackIosNewRounded fontSize="xLarge" color="inherit" />
        </Button>
        <div className={styles.Carousel} ref={carouselRef}>
          <div className={styles.CarouselContent}>
            {slides.map((video, index) => (
              <Slide
                key={index}
                className={styles.Slide}
                isActive={activeSlideMediaId === video.mediaid}
                activeCustomContent={activeCustomContent}
                video={video}
                channel={channel}
                width={dimensions.width}
                height={dimensions.height}
                onClick={(event, mediaId) => {
                  scrollToIndex(index);
                  onSlideClick(event, index, mediaId);
                }}
                onDoubleClick={(event) => onSlideDoubleClick(event, index, video.mediaid)}
                lazyLoading={window.innerWidth / dimensions.width <= index}
                showContent={isInRange(index, scrollMeta.visibleStart, scrollMeta.visibleEnd)}
              />
            ))}
          </div>
        </div>
        <Button
          className={classNames(styles.Arrow, styles.Arrow___next)}
          variant="text"
          shape="square"
          color="inherit"
          size="xSmall"
          disabled={!scrollMeta.scrollNext}
          onClick={() => scrollBySlides(1)}
          aria-label={t.nextSlideVideos.text()}
          style={{
            height: dimensions.height,
          }}
        >
          <ArrowForwardIosRounded fontSize="xLarge" color="inherit" />
        </Button>
      </div>
    );
  },
);

export const Skeleton = CarouselSkeleton;

export default Carousel;
